import { useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle } from 'lucide-react';
import { STATUS_META, statusBadgeClass } from '../../utils/statusMeta';
import { AttentionItem } from './supplierStatus';
import { formatDateOnly } from './deadlineChip';

const COLLAPSED = 5;

const actionText: Record<string, string> = {
  rejected: 'Corregir',
  correction_requested: 'Corregir',
  overdue: 'Entregar',
};

/** "Atención requerida" summary shown at the top of the supplier dashboard. */
export default function AttentionPanel({ items, onOpen }: { items: AttentionItem[]; onOpen: (item: AttentionItem) => void }) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) {
    return (
      <section aria-label="Atención requerida" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
        <CheckCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p><strong className="font-semibold">Todo al día.</strong> No tienes documentos rechazados, con corrección solicitada ni vencidos.</p>
      </section>
    );
  }

  const visible = expanded ? items : items.slice(0, COLLAPSED);
  return (
    <section aria-labelledby="attention-title" className="overflow-hidden rounded-lg border border-amber-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3">
        <h2 id="attention-title" className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          Atención requerida
          <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900" aria-label={`${items.length} pendientes`}>{items.length}</span>
        </h2>
        <p className="text-xs text-amber-800">Documentos que necesitan una acción tuya.</p>
      </div>
      <ul className="divide-y divide-slate-100">
        {visible.map((item) => (
          <li key={item.key} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-900">{item.documentName}</span>
                <span className="text-xs text-slate-500">{item.period}</span>
                <span className={statusBadgeClass(item.status)}>{STATUS_META[item.status].label}</span>
              </div>
              {item.reason && <p className="mt-1 line-clamp-2 text-xs text-slate-600">Motivo del revisor: {item.reason}</p>}
              {item.status === 'overdue' && item.deadline && <p className="mt-1 text-xs text-slate-600">La fecha límite fue el {formatDateOnly(item.deadline)}.</p>}
            </div>
            <button
              type="button"
              onClick={() => onOpen(item)}
              aria-label={`${actionText[item.status] || 'Abrir'} ${item.documentName} · ${item.period}`}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500 sm:self-auto"
            >
              {actionText[item.status] || 'Abrir'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {items.length > COLLAPSED && (
        <div className="border-t border-slate-100 px-4 py-2">
          <button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)} className="text-sm font-medium text-blue-700 hover:underline">
            {expanded ? 'Ver menos' : `Ver todos (${items.length})`}
          </button>
        </div>
      )}
    </section>
  );
}
